import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, Clock } from "lucide-react";
import clsx from "clsx";
import { Button } from "components/common/Button";

interface GroupsApprovalBarV3Props {
  summaryText: string;
  allApproved: boolean;
  onToggleApproval: () => void;
  isLoading?: boolean;
  canEdit: boolean;
  startAdornment?: ReactNode;
  /** Rendered inline just to the right of the summary text. */
  summaryAdornment?: ReactNode;
  /** Rendered at the end of the buttons row. */
  endAdornment?: ReactNode;
}

export const GroupsApprovalBarV3 = ({
  summaryText,
  allApproved,
  onToggleApproval,
  isLoading = false,
  canEdit,
  startAdornment,
  summaryAdornment,
  endAdornment,
}: GroupsApprovalBarV3Props) => {
  const { t } = useTranslation();

  return (
    <div className="w-full flex flex-col gap-y-1.5 px-1 py-2 mb-1 border-b border-gray-100">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span
          className={clsx(
            "flex items-center gap-1.5 whitespace-nowrap text-sm transition-colors",
            isLoading
              ? "text-gray-400 font-medium animate-pulse"
              : allApproved
                ? "text-green-600 font-semibold"
                : "text-amber-600 font-medium",
          )}
        >
          {allApproved && !isLoading ? (
            <CheckCircle size={14} className="flex-shrink-0" />
          ) : (
            <Clock size={14} className="flex-shrink-0" />
          )}
          {summaryText}
        </span>
        {summaryAdornment}
      </div>
      <div className="flex flex-wrap justify-start items-center gap-1">
        {startAdornment}
        {!isLoading && canEdit && (
          <Button
            variant={allApproved ? "danger" : "success"}
            size="sm"
            onClick={onToggleApproval}
          >
            {allApproved
              ? t("smartSurveyor.reviewPage.unapproveAll")
              : t("smartSurveyor.reviewPage.approveAll")}
          </Button>
        )}
        {endAdornment}
      </div>
    </div>
  );
};
